import React, { createContext, useContext, useState, useEffect } from 'react';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';

interface AuthContextType {
  isAuthenticated: boolean;
  user: any;
  loading: boolean;
  login: (returnTo?: string) => void;
  logout: () => void;
  getToken: () => Promise<string>;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  user: null,
  loading: true,
  login: () => {},
  logout: () => {},
  getToken: async () => ''
});

export const AuthProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const { 
    isAuthenticated, 
    user, 
    loginWithRedirect, 
    logout: auth0Logout,
    getAccessTokenSilently,
    isLoading 
  } = useAuth0();

  // Login with optional returnTo URL (passed via Auth0 appState)
  const login = (returnTo?: string) => {
    loginWithRedirect({
      appState: { returnTo: returnTo || window.location.pathname }
    });
  };

  const getToken = async () => {
    try {
      return await getAccessTokenSilently();
    } catch (error: any) {
      console.error('Error getting token:', error);

      // Check if this is a login required error or token refresh failure
      if (error?.error === 'login_required' ||
          error?.error === 'consent_required' ||
          error?.message?.includes('Missing Refresh Token') ||
          error?.message?.includes('Invalid state')) {
        console.error('Auth0 session expired or invalid - forcing logout');
        // Force logout to clear invalid session
        auth0Logout({
          logoutParams: {
            returnTo: window.location.origin
          }
        });
      }

      return '';
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        loading: isLoading,
        login,
        logout: () => auth0Logout({ logoutParams: { returnTo: window.location.origin } }),
        getToken
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const Auth0Wrapper: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const onRedirectCallback = (appState?: { returnTo?: string }) => {
    console.log('[Auth0] Redirect callback triggered', appState);
    const targetPath = appState?.returnTo || '/';

    // If we need to go to a different path, use location.replace to trigger actual navigation
    // This is needed because Auth0Wrapper is outside the Router context
    if (targetPath !== '/' && targetPath !== window.location.pathname) {
      window.location.replace(targetPath);
    } else {
      // Just clean up the URL (remove code/state params) for the current path
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  return (
    <Auth0Provider
      domain={process.env.REACT_APP_AUTH0_DOMAIN || ''}
      clientId={process.env.REACT_APP_AUTH0_CLIENT_ID || ''}
      authorizationParams={{
        redirect_uri: process.env.REACT_APP_AUTH0_REDIRECT_URI || window.location.origin,
        audience: process.env.REACT_APP_AUTH0_AUDIENCE
      }}
      onRedirectCallback={onRedirectCallback}
      cacheLocation="localstorage"
      useRefreshTokens={true}
    >
      <AuthProvider>{children}</AuthProvider>
    </Auth0Provider>
  );
};

export const useAuth = () => useContext(AuthContext); 